import { Component, HostListener, inject, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import {
  AppComms,
  Cognito,
  DatadogService,
  EnvService,
  IFrameService,
  LoggerService,
  SpinnerService,
  User
} from '@enliven/angular';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import packageInfo from '../../package.json';
import {
  ACTIVE_USER,
  COGNITO,
  E360_USER,
  MAX_IDLE_TIMEOUT_MINS,
  NEXT_ORG_E360_URL,
  ORG_DEFAULT_MODULE_ID,
  SESSION_TIMEOUT_MINS,
  SIGN_IN_URL,
  SSO_SIGN_OUT_MSG,
  SSO_USER_SIGNED_IN
} from './constants/storage-constants';
import { AmplifyService } from './services/amplify.service';
import { CognitoService } from './services/cognito.service';
import { DataService } from './services/data.service';
import { E360SessionService } from './services/e360-session.service';
import { E360UserService } from './services/e360-user.service';
import { StateService } from './services/state.service';
import { UrlParamService } from './services/url-param.service';
import { API_USER_DEACTIVATED } from './constants/api-error-constants';

@Component({
  selector: 'e360-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [RouterOutlet]
})
export class AppComponent implements OnInit {
  private readonly router: Router = inject(Router);
  private readonly cognitoService: CognitoService = inject(CognitoService);
  private readonly e360SessionService: E360SessionService = inject(E360SessionService);
  private readonly iFrameService: IFrameService = inject(IFrameService);
  private readonly datadogService: DatadogService = inject(DatadogService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dataService: DataService = inject(DataService);
  private readonly amplifyService: AmplifyService = inject(AmplifyService);
  private readonly userService: E360UserService = inject(E360UserService);
  private readonly spinnerService: SpinnerService = inject(SpinnerService);
  private readonly envService: EnvService = inject(EnvService);
  private readonly urlParams: UrlParamService = inject(UrlParamService);
  private readonly stateService: StateService = inject(StateService);
  private readonly logger: LoggerService = inject(LoggerService);

  readonly AMPLIFY_STORAGE_PREFIX: string = 'CognitoIdentityServiceProvider';

  isASignInPath: boolean;
  isASignOutPath: boolean;
  readyForRouting: boolean = false;
  isEmptyPath: boolean;

  constructor() {
    this.translate.addLangs(['en']);
    this.translate.setDefaultLang('en');

    // clear spinner in shell app (IP-14420 Issue fix)
    this.router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError
        )
      )
      .subscribe(() => this.spinnerService.setSpinner(false));
  }

  @HostListener('window:message', ['$event'])
  onMessage({ data }: MessageEvent) {
    if (data.for === AppComms.SHELL_APP_NAME) {
      switch (data.operation) {
        case AppComms.CHANGE_ROUTE: {
          this.iFrameService.replaceShellState(data.message);
          break;
        }
        case AppComms.PASS_SESSION_DETAILS: {
          this.spinnerService.setSpinner(false);
          this.iFrameService.sendMessageToIFrame(AppComms.PASS_SESSION_DETAILS, this.e360SessionService.session);
          break;
        }
        case AppComms.PASS_USER_DETAILS: {
          this.iFrameService.sendMessageToIFrame(AppComms.PASS_USER_DETAILS, this.userService.user);
          break;
        }
        case AppComms.PASS_ORG_DETAILS: {
          this.iFrameService.sendMessageToIFrame(AppComms.PASS_ORG_DETAILS, this.stateService.selectedOrg);
          break;
        }
        case AppComms.PASS_ORG_STORE_DETAILS: {
          this.iFrameService.sendMessageToIFrame(AppComms.PASS_ORG_STORE_DETAILS, this.stateService.selectedOrgStore);
          break;
        }
        case AppComms.PASS_ORGS_LIST: {
          this.iFrameService.sendMessageToIFrame(AppComms.PASS_ORGS_LIST, this.stateService.orgs);
          break;
        }
        case AppComms.PASS_ORG_STORES_LIST: {
          this.iFrameService.sendMessageToIFrame(AppComms.PASS_ORG_STORES_LIST, this.stateService.orgStores);
          break;
        }
      }
    }
  }

  ngOnInit(): void {
    this.determinePath();

    this.initializeServices();

    if (this.isActiveUserRefreshing()) return;
    if (this.isStaleSSOUserInStorage()) return;
    if (this.isStaleDirectLoginUserInStorage()) return;
    if (this.shouldRedirectAfterCognitoSignOut()) return;
    if (this.redirectFromCognitoWithErrors()) return;
    if (this.redirectFromCognitoWithCode()) return;
    if (this.redirectFromCognitoAfterSignOut()) return;

    this.fetchCognitoInfo().subscribe(cognito => {
      if (!cognito) return;

      this.mapUrlParamsAndCognitoToSession(cognito);

      if (this.redirectFromEHAppAfterSignout(cognito)) return;
      if (this.isUserSigningIn(cognito)) return;

      sessionStorage.clear();
      this.readyForRouting = true;
    });
  }

  private initializeServices(): void {
    this.urlParams.handleURLParams(this.getSearch());
    this.datadogService.init({
      applicationId: 'c3ce33c5-e576-48c9-8627-9dfba59c4c06',
      clientToken: 'pubb618baa68126fcf5d6f57452c4d03d7b',
      service: packageInfo.name,
      version: packageInfo.version
    });
  }

  private redirectFromCognitoWithErrors(): boolean {
    const { cognitoError, cognitoErrorDescription } = this.urlParams;
    if (cognitoError || cognitoErrorDescription) {
      this.logger.error('Cognito - authorization error during SSO log in attempt', {
        cognitoError,
        cognitoErrorDescription
      });
      sessionStorage.clear();
      this.readyForRouting = true;
      this.router.navigate(['error'], { state: { message: 'ALERT_PAGE.SSO_SIGN_IN_FAILED' } });
      return true;
    }
    return false;
  }

  private fetchCognitoInfo(): Observable<Cognito> {
    const origin: string = this.getOrigin();
    let search: string = this.urlParams.orgIdpExternalId;
    if (!search) {
      search = this.envService.isLocal ? this.envService.commonPlatformCognito.websiteBaseUrl : origin;
    }

    search = encodeURIComponent(search);

    return this.dataService.getCognitoInfoByOrg(search).pipe(
      map(data => {
        const orgWebsiteUrl: string = data.websiteBaseUrl;
        if (!this.envService.isLocal && orgWebsiteUrl !== origin) {
          this.logger.error('User is accessing E360 through incorrect URL for their org', { orgWebsiteUrl, origin });
          this.readyForRouting = true;
          this.router.navigate(['error'], { state: { message: 'ALERT_PAGE.INCORRECT_ORGANIZATION' } });
          return null;
        }
        return data;
      }),
      catchError(err => {
        if (err.status === 400) {
          return of(this.envService.commonPlatformCognito);
        } else {
          this.logger.error('Error retrieving Cognito info', { search }, err);
          this.readyForRouting = true;
          this.router.navigate(['error'], { state: { message: 'ALERT_PAGE.DEFAULT_ERROR' } });
          return of(null);
        }
      })
    );
  }

  private isUserSigningIn(cognito: Cognito): boolean {
    if (this.isASignInPath) {
      this.continueWithSignIn(cognito);
      return true;
    }
    return false;
  }

  private continueWithSignIn(cognito: Cognito) {
    if (cognito.isSSO) {
      this.urlParams.saveToStorage();
      this.cognitoService.authorize(cognito);
    } else {
      this.amplifyService.configureAmplify(cognito);
      this.readyForRouting = true;
    }
  }

  private redirectFromCognitoWithCode(): boolean {
    const { code } = this.urlParams;
    if (code) {
      this.handleSsoSignIn();
      return true;
    }
    return false;
  }

  private handleSsoSignIn() {
    this.e360SessionService.setSessionFromStorage();
    this.cognitoService.setUserPoolInfo(this.e360SessionService.session.cognito);
    this.cognitoService.authenticateViaGrantCode(this.urlParams.code).subscribe({
      next: (resp: { isAuthenticated: boolean; error: string }) => {
        if (resp.isAuthenticated) {
          this.readyForRouting = true;
          this.router.navigate(['/app']);
        } else {
          const message =
            resp.error === API_USER_DEACTIVATED ? 'ALERT_PAGE.SSO_USER_DEACTIVATED' : 'ALERT_PAGE.SSO_SIGN_IN_FAILED';
          this.cognitoService.signOut({ message: message });
        }
      }
    });
  }

  private isActiveUserRefreshing(): boolean {
    if (!this.isASignOutPath && !this.isASignInPath && sessionStorage.getItem(ACTIVE_USER) === 'true') {
      this.handleUserRefresh();
      return true;
    }
    return false;
  }

  private handleUserRefresh() {
    this.e360SessionService.appReloaded();
    this.e360SessionService.organizationDefaultModuleId = sessionStorage.getItem(ORG_DEFAULT_MODULE_ID);
    this.e360SessionService.maxIdleTimeoutMinutes = +sessionStorage.getItem(MAX_IDLE_TIMEOUT_MINS);
    this.e360SessionService.sessionTimeoutMinutes = +sessionStorage.getItem(SESSION_TIMEOUT_MINS);
    this.userService.user = JSON.parse(sessionStorage.getItem(E360_USER)) as User;
    const { cognito } = this.e360SessionService.session;
    if (cognito.isSSO) {
      this.cognitoService.setUserPoolInfo(cognito);
    } else {
      this.amplifyService.configureAmplify(cognito);
    }
    this.readyForRouting = true;
  }

  // This method covers two use cases
  // A stale SSO user is auto signed out, and E360 is logging in the active user
  // An SSO user is accessing a module in a different org, has been signed out, and is being logged into the next org
  private shouldRedirectAfterCognitoSignOut(): boolean {
    const nextUrl: string = localStorage.getItem(NEXT_ORG_E360_URL);
    if (nextUrl) {
      this.navigateToE360Url(nextUrl);
      return true;
    }
    return false;
  }

  private navigateToE360Url(appEntryPoint: string) {
    localStorage.removeItem(COGNITO);
    localStorage.removeItem(SSO_SIGN_OUT_MSG);
    localStorage.removeItem(SIGN_IN_URL);
    localStorage.removeItem(SSO_USER_SIGNED_IN);
    localStorage.removeItem(NEXT_ORG_E360_URL);
    sessionStorage.clear();
    window.open(appEntryPoint, '_self');
  }

  private isStaleDirectLoginUserInStorage(): boolean {
    if (Object.keys(localStorage).some(k => k.includes(this.AMPLIFY_STORAGE_PREFIX))) {
      this.signOutStaleDirectUser();
      return true;
    }
    return false;
  }

  private async signOutStaleDirectUser() {
    const cognito: Cognito = JSON.parse(localStorage.getItem(COGNITO));
    if (cognito) {
      this.amplifyService.configureAmplify(cognito);
      // Try to revoke refresh token if we have it
      const refreshToken = this.e360SessionService.refreshToken;
      if (refreshToken) {
        this.amplifyService
          .revokeRefreshToken(refreshToken)
          .pipe(
            finalize(() => {
              this.amplifyService.cleanStorageAfterSignOut();
              this.navigateToE360Url(this.getHref());
            })
          )
          .subscribe({
            next: () => this.logger.info('Stale refresh token revoked successfully'),
            error: err => this.logger.warn('Failed to revoke stale refresh token, continuing cleanup', {}, err)
          });
      } else {
        this.amplifyService.cleanStorageAfterSignOut();
        this.navigateToE360Url(this.getHref());
      }
    } else {
      const amplifyStorageKeys: string[] = Object.keys(localStorage).filter(k =>
        k.includes(this.AMPLIFY_STORAGE_PREFIX)
      );
      amplifyStorageKeys.push('amplify-signin-with-hostedUI');
      amplifyStorageKeys.forEach(k => localStorage.removeItem(k));
      this.navigateToE360Url(this.getHref());
    }
  }

  private isStaleSSOUserInStorage(): boolean {
    if (!sessionStorage.length && JSON.parse(localStorage.getItem(SSO_USER_SIGNED_IN)) === true) {
      this.signOutStaleSSOUser();
      return true;
    }
    return false;
  }

  private mapUrlParamsAndCognitoToSession(cognito: Cognito) {
    const { defaultStoreId, defaultStoreIdType, orgIdpExternalId } = this.urlParams;
    this.e360SessionService.setCognitoInfo(cognito);
    this.e360SessionService.orgIdpExternalId = orgIdpExternalId;
    this.e360SessionService.orgId = cognito.organizationId;
    this.e360SessionService.setDefaultStore({
      storeId: defaultStoreId,
      storeIdType: defaultStoreIdType
    });
    this.e360SessionService.saveToStorage();
    localStorage.setItem(COGNITO, JSON.stringify(cognito)); // for stale user handling
  }

  private signOutStaleSSOUser() {
    localStorage.setItem(NEXT_ORG_E360_URL, this.getHref());
    this.cognitoService.setUserPoolInfo(JSON.parse(localStorage.getItem(COGNITO)));
    this.cognitoService.signOut({ message: 'ALERT_PAGE.STALE_USER_SIGN_OUT' });
  }

  private redirectFromCognitoAfterSignOut(): boolean {
    if (this.isASignOutPath && !this.urlParams.from && !localStorage.getItem(NEXT_ORG_E360_URL)) {
      this.readyForRouting = true;
      return true;
    }
    return false;
  }

  // Ex) User signs out of OPE in OPE, OPE redirects back to E360 to display the E360 sign out page instead of OPE sign out page
  private redirectFromEHAppAfterSignout(cognito: Cognito): boolean {
    if (this.isASignOutPath && this.urlParams.from) {
      if (cognito.isSSO) {
        this.cognitoService.setUserPoolInfo(cognito);
        this.cognitoService.buildReSignInLink();
      } else {
        this.amplifyService.configureAmplify(cognito);
        this.router.navigate(['signin'], {
          queryParams: { signedOut: 'true' } // Signal explicit sign-out to prevent session restoration
        });
      }
      this.readyForRouting = true;
      return true;
    }
    return false;
  }

  private determinePath(): void {
    const lowerPath: string = this.getPathname();
    this.isASignInPath = lowerPath === '/' || lowerPath.includes('signin');
    this.isEmptyPath = lowerPath.startsWith('/?');
    this.isASignOutPath = lowerPath.includes('signout');
  }

  getHref(): string {
    return window.location.href;
  }

  getOrigin(): string {
    return window.location.origin;
  }

  getPathname(): string {
    return window.location.pathname.toLowerCase();
  }

  getSearch(): string {
    return window.location.search;
  }
}
